import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Container,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { fetchClients } from '../../actions/Clients';

import { getVisibleItemsText } from '../../helpers';
import { handleComponentParams, handleURLParams } from '../../helpers/URLParams';

import AdminPagination from '../../components/Admin/Pagination';
import AdminPanelHeaderInfo from '../../components/AdminPanel/HeaderInfo';
import AdminPanelClientList from '../../components/AdminPanel/ClientList';

const perPage = 20;

const AdminPanelAccounts = ({ history, location }) => {
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('');

  let searchRef = useRef();

  const intl = useIntl();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    handleComponentParams(
      true,
      ['page', 'orderBy', 'order', 'search', 'search'],
      null,
      [setPage, setOrderBy, setOrder, setSearch, setSearchField],
      () => {},
      { order, orderBy },
    );

    if (!queryParams.get('orderBy')) {
      handleURLParams({ orderBy }, history);
    }

    if (!queryParams.get('order')) {
      handleURLParams({ order }, history);
    }

    setClientsLoading(false);
  }, [history, location.search, order, orderBy]);

  const loadClients = useCallback((silent = false) => {
    if (!silent) {
      setClientsLoading(true);
    }

    const params = {
      page,
      per_page: perPage,
      order_by: orderBy,
      order,
      search,
    };
    fetchClients(params).then((response) => {
      setClients(response.data);
      setTotalClients(response.meta.total);
      setClientsLoading(false);
    });
  }, [page, orderBy, order, search]);

  useEffect(() => {
    if (clientsLoading) {
      return;
    }

    loadClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, orderBy, order, search]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeOrder = (newOrderBy, newOrder) => {
    handleURLParams({ orderBy: newOrderBy, order: newOrder }, history);

    setOrderBy(newOrderBy);
    setOrder(newOrder);
    setPage(1);
  };

  const handleChangeSearch = (e) => {
    const { value } = e.target;
    setSearchField(value);
  };

  const handleClearSearch = (e) => {
    e.preventDefault();
    searchRef.value = '';

    handleURLParams({ search: '', page: 1 }, history);

    setSearch('');
    setSearchField('');
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      setSearch(searchField);
      handleURLParams({ search: searchField, page: 1 }, history);

      setPage(1);
    }
  };

  const afterClientDeleted = (client) => {
    setClients((prevClients) => (prevClients.filter((c) => c.uuid !== client.uuid)));
    loadClients(true);
  };

  const afterClientUpdated = () => {
    loadClients(true);
  };

  return (
    <div className="main-content split-content page-accounts">
      <div className="split-content-wrapper">
        <Container fluid>
          <header className="page-header">
            <h2 className="page-title">
              <i className="far fa-file-user" />
              {intl.formatMessage({ defaultMessage: 'Accounts' })}
            </h2>
            <AdminPanelHeaderInfo
              loading={clientsLoading}
              itemsTotal={totalClients}
              itemsVisible={getVisibleItemsText(page, perPage, totalClients)}
              itemTitle="account"
            />
          </header>
          <div className="content-wrapper admin-panel-wrapper admin-accounts-wrapper">
            <div className="actions-wrapper">
              <div className="position-relative inline-search">
                <Input
                  type="text"
                  name="search"
                  bsSize="sm"
                  innerRef={(ref) => { searchRef = ref; }}
                  placeholder={intl.formatMessage({ defaultMessage: 'Type name and press Enter to search' })}
                  disabled={clientsLoading}
                  onChange={handleChangeSearch}
                  onKeyPress={handleKeyPress}
                  value={searchField || ''}
                />
                {search && (
                  <a
                    href="#clear"
                    className="search-clear"
                    onClick={handleClearSearch}
                  >
                    <i className="fas fa-times" />
                  </a>
                )}
              </div>
            </div>
            <AdminPanelClientList
              clients={clients}
              loading={clientsLoading}
              orderBy={orderBy}
              order={order}
              afterClientDeleted={afterClientDeleted}
              afterClientUpdated={afterClientUpdated}
              handleChangeOrder={handleChangeOrder}
            />
            <div className="table-footer">
              <AdminPagination
                total={totalClients}
                page={page}
                perPage={perPage}
                handleChangePage={handleChangePage}
              />
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

AdminPanelAccounts.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(AdminPanelAccounts);
